import { useState, useEffect } from "react";
import api from "../../services/api";

const STYLES = `
  @keyframes etExpand {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .et-toggle:hover { background: rgba(102,126,234,0.12) !important; }
  .et-copilot:hover { opacity: 0.85; transform: translateY(-1px); }
`;

/**
 * EligibilityPlainText
 * 
 * Props:
 *   trialId      — number — required
 *   nctId        — string — for Copilot prompt
 *   trialName    — string — for Copilot prompt
 *   defaultOpen  — bool  — expand by default (e.g. on NLP search)
 *   compact      — bool  — compact mode for small cards
 */
export default function EligibilityPlainText({
  trialId, nctId, trialName, defaultOpen = false, compact = false
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || data || !trialId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/v1/admin/translations/trial/${trialId}`);
        if (res.data.hasTranslation) {
          console.log("Translation raw:", res.data.translation);
          console.log("Translation type:", typeof res.data.translation);
          const parsed = typeof res.data.translation === "string"
            ? JSON.parse(res.data.translation)
            : res.data.translation;
            console.log("Parsed translation:", parsed);
            console.log("may_qualify:", parsed?.may_qualify);
          setData(parsed);
        } else {
          
          setData(null);
        }
      } catch (e) {
        console.error("Parse error:",e);
        setError("Could not load eligibility information");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, trialId, data]);

  const askCopilot = () => {
    let prompt = `I am looking at the ${trialName || nctId} clinical trial (${nctId}).`;
  
  // Include the plain English translation if we have it
  if (data) {
    if (data.summary) {
      prompt += ` This trial is for: ${data.summary}.`;
    }
    if (data.may_qualify?.length > 0) {
      prompt += ` I may qualify if: ${data.may_qualify.join('; ')}.`;
    }
    if (data.may_not_qualify?.length > 0) {
      prompt += ` I may NOT qualify if: ${data.may_not_qualify.join('; ')}.`;
    }
  }
  
  prompt += ` Based on my profile and diagnosis, do you think I qualify for this trial? What should I ask my doctor about it?`;

    window.dispatchEvent(new CustomEvent("copilot-preload", {
      detail: { prompt }
    }));
  };

  if (!trialId) return null;

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{STYLES}</style>

      {/* Toggle button */}
      <button
        className="et-toggle"
        onClick={() => setOpen(o => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: open ? "rgba(102,126,234,0.1)" : "rgba(102,126,234,0.06)",
          border: "1px solid rgba(102,126,234,0.2)",
          borderRadius: 999, padding: compact ? "4px 12px" : "5px 14px",
          fontSize: compact ? 11 : 12, fontWeight: 600, color: "#1A5EA8",
          cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s",
        }}
      >
        <span>{open ? "▲" : "▼"}</span>
        <span>Do I Qualify? — Plain English</span>
        {!open && (
          <span style={{
            fontSize: 9, fontWeight: 700, background: "#1A5EA8", color: "white",
            borderRadius: 999, padding: "1px 6px", textTransform: "uppercase", letterSpacing: "0.06em"
          }}>AI</span>
        )}
      </button>

      {/* Content panel */}
      {open && (
        <div style={{
          marginTop: 10,
          background: "linear-gradient(135deg, #f8faff, #f5f3ff)",
          border: "1px solid rgba(102,126,234,0.2)",
          borderRadius: 12, padding: compact ? "14px 16px" : "18px 20px",
          animation: "etExpand 0.25s ease both",
        }}>

          {loading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#9ca3af", fontSize: 13 }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid #e5e7eb", borderTopColor: "#1A5EA8", animation: "spin 0.7s linear infinite" }} />
              Loading eligibility information...
            </div>
          )}

          {error && (
            <p style={{ fontSize: 13, color: "#dc2626" }}>{error}</p>
          )}

          {!loading && !error && !data && (
            <div style={{ fontSize: 13, color: "#9ca3af" }}>
              <p style={{ margin: "0 0 10px" }}>Plain English eligibility not yet available for this trial.</p>
              <button className="et-copilot"
                onClick={askCopilot}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "7px 14px", background: "linear-gradient(135deg,#1A5EA8,#0B2545)",
                  color: "white", border: "none", borderRadius: 8,
                  fontSize: 12, fontWeight: 600, cursor: "pointer",
                  fontFamily: "inherit", transition: "all 0.2s",
                }}>
                🤝 Ask Care Copilot if I qualify
              </button>
            </div>
          )}

          {!loading && !error && data && (
            <div>
              {/* Summary */}
              {data.summary && (
                <p style={{
                  fontSize: compact ? 12 : 13, color: "#374151", fontStyle: "italic",
                  margin: "0 0 12px", lineHeight: 1.6,
                  paddingBottom: 10, borderBottom: "1px solid rgba(102,126,234,0.15)"
                }}>
                  {data.summary}
                </p>
              )}

              <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr" : "1fr 1fr", gap: 12 }}>
                {/* May qualify */}
                {data.may_qualify?.length > 0 && (
                  <div>
                    <p style={{
                      fontSize: 10, fontWeight: 700, color: "#059669",
                      textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 8px"
                    }}>
                      ✅ You may qualify if
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {data.may_qualify.map((point, i) => (
                        <div key={i} style={{
                          display: "flex", gap: 8, alignItems: "flex-start",
                          background: "rgba(5,150,105,0.06)", border: "1px solid rgba(5,150,105,0.15)",
                          borderRadius: 8, padding: "7px 10px",
                        }}>
                          <span style={{ color: "#059669", flexShrink: 0, fontSize: 13 }}>✓</span>
                          <span style={{ fontSize: compact ? 12 : 13, color: "#374151", lineHeight: 1.5 }}>{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* May not qualify */}
                {data.may_not_qualify?.length > 0 && (
                  <div>
                    <p style={{
                      fontSize: 10, fontWeight: 700, color: "#dc2626",
                      textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 8px"
                    }}>
                      ❌ You may not qualify if
                    </p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {data.may_not_qualify.map((point, i) => (
                        <div key={i} style={{
                          display: "flex", gap: 8, alignItems: "flex-start",
                          background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.12)",
                          borderRadius: 8, padding: "7px 10px",
                        }}>
                          <span style={{ color: "#dc2626", flexShrink: 0, fontSize: 13 }}>✗</span>
                          <span style={{ fontSize: compact ? 12 : 13, color: "#374151", lineHeight: 1.5 }}>{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Ask Copilot CTA */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(102,126,234,0.15)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                <p style={{ fontSize: 11, color: "#9ca3af", margin: 0, fontStyle: "italic" }}>
                  AI-translated for patient understanding · Always confirm eligibility with your care team
                </p>
                <button className="et-copilot"
                  onClick={askCopilot}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 14px", background: "linear-gradient(135deg,#1A5EA8,#0B2545)",
                    color: "white", border: "none", borderRadius: 8,
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                    fontFamily: "inherit", transition: "all 0.2s",
                    boxShadow: "0 2px 10px rgba(102,126,234,0.35)",
                  }}>
                  🤝 Ask Copilot if I qualify
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}